import React, { useState, useEffect, useMemo } from 'react'
import { MaterialReactTable } from 'material-react-table'
import {
  Button,
  Typography,
  Modal,
  TextField,
  Card,
  CardContent,
  CardActions,
  Box,
  Paper,
  makeStyles,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core'
import { CloudUpload, Add, Search, Edit } from '@material-ui/icons'
import { toast } from 'react-toastify'
import {
  getParticipantes,
  sendFileService,
  getProductoById,
  addProducto,
  updateRecompensa
} from '@auteco/services/recompensasService'

const useStyles = makeStyles((theme) => ({
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  searchButton: {
    marginTop: theme.spacing(2),
  },
  cardContent: {
    '& > p': {
      marginBottom: theme.spacing(1),
    },
  },
  cardActions: {
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
  },
}))

const RutaProductosRecompensasContainer = () => {
  const classes = useStyles()
  const [productos, setProductos] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [idInput, setIdInput] = useState('')
  const [idCard, setIdCard] = useState(null)
  const [error, setError] = useState('')
  const [showActivos, setShowActivos] = useState(true)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [newReward, setNewReward] = useState('')

  const getProductosData = async () => {
    setIsLoading(true)
    const response = await getParticipantes()
    if (response) {
      const sortedProductos = response.participantes.sort((a, b) => 
        new Date(b.fecha) - new Date(a.fecha)
      )
      setProductos(sortedProductos)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    getProductosData()
  }, [])

  const handleFileUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await sendFileService(formData)
      if (response && !response.data.error) {
        toast.success('Archivo enviado correctamente')
      } else {
        toast.error('Error al enviar el archivo')
      }
    } catch (err) {
      toast.error('Error al leer el CSV')
    }
    event.target.value = null
    getProductosData()
  }


  const handleBuscar = async () => {
    setError('')
    const response = await getProductoById(idInput)
    if (response.repuesto && response.repuesto.length > 0) {
      setIdCard({
        id: idInput,
        ...response.repuesto[0],
      })
    } else {
      setError('No se encontró el repuesto')
      toast.error('No se encontró el repuesto')
      setIdCard(null)
    }
    setIdInput('')
  }

  const handleAgregar = async (body) => {
    const response = await addProducto(body)
    if (response) {
      toast.success('Producto actualizado correctamente')
    } else {
      toast.error('Error al agregar el producto')
    }
    setModalOpen(false)
    setIdCard(null)
  }

  const handleEditReward = async () => {
    if (!selectedProduct || !newReward) return

    const response = await updateRecompensa({
      repuesto_id: selectedProduct.repuesto_id,
      recompensa: newReward
    })

    if (response) {
      toast.success('Recompensa actualizada correctamente')
      getProductosData()
    } else {
      toast.error('Error al actualizar la recompensa')
    }

    setEditModalOpen(false)
    setSelectedProduct(null)
    setNewReward('')
  }

  const formatPrice = (price) => new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price)

  const columns = useMemo(
    () => [
      {
        accessorKey: 'repuesto_id',
        header: 'Producto',
      },
      {
        accessorKey: 'nombre_repuesto',
        header: 'Nombre',
      },
      {
        accessorKey: 'recompensa',
        header: 'Recompensa',
        Cell: ({ cell }) => formatPrice(cell.getValue()),
      },
      {
        accessorKey: 'fecha',
        header: 'Fecha',
      },
      {
        accessorKey: 'actions',
        header: 'Acciones',
        Cell: ({ row }) => (
          <Button
            startIcon={<Edit />}
            onClick={() => {
              setSelectedProduct(row.original)
              setNewReward(row.original.recompensa)
              setEditModalOpen(true)
            }}
          >
            Editar
          </Button>
        ),
      },
    ],
    []
  )

  const filteredProductos = useMemo(() => {
    if (!showActivos) return productos
    return productos.filter(producto => producto.recompensa > 0)
  }, [productos, showActivos])

  return (
    <div className="productos-container">
      <Box mb={3}>
        <Typography variant="h5" className={classes.header}>
          Productos Recompensas
        </Typography>
      </Box>

      <div className={`table-container ${isLoading ? 'loading' : ''}`}>
        <Box mb={3} className={classes.buttonGroup}>
          <input
            accept=".csv"
            style={{ display: 'none' }}
            id="contained-button-file"
            type="file"
            onChange={handleFileUpload}
          />
          <label htmlFor="contained-button-file">
            <Button
              variant="contained"
              color="primary"
              component="span"
              startIcon={<CloudUpload />}
            >
              Cargar CSV
            </Button>
          </label>
          <Box display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={showActivos}
                  onChange={(e) => setShowActivos(e.target.checked)}
                  color="primary"
                />
              }
              label="Activos"
            />
          </Box>
        </Box>

        <MaterialReactTable
          columns={columns}
          data={filteredProductos}
          state={{ isLoading }}
          enableColumnActions={false}
          enableColumnFilters={false}
          enablePagination
          enableSorting
          muiTableProps={{
            sx: {
              tableLayout: 'fixed',
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: '#F6F6F6',
              color: '#161615',
              fontSize: '14px',
              fontWeight: 700,
            },
          }}
          initialState={{
            pagination: { pageSize: 10, pageIndex: 0 },
            density: 'compact',
          }}
          renderTopToolbarCustomActions={() => null}
        />
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Paper className={classes.modal}>
          <Typography variant="h6" className={classes.header}>
            Agregar Producto
          </Typography>
          <Box mb={3}>
            <TextField
              fullWidth
              label="ID del Producto"
              variant="outlined"
              value={idInput}
              onChange={(e) => setIdInput(e.target.value)}
              error={!!error}
              helperText={error}
            />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleBuscar}
              className={classes.searchButton}
              startIcon={<Search />}
            >
              Buscar
            </Button>
          </Box>
          {idCard && (
            <Card>
              <CardContent className={classes.cardContent}>
                <Typography variant="h6" gutterBottom>
                  ID: {idCard.id}
                </Typography>
                <Typography>
                  <strong>Nombre:</strong> {idCard.Descripcion_Material}
                </Typography>
                <Typography>
                  <strong>Categoría:</strong> {idCard.Categoria}
                </Typography>
                <Typography>
                  <strong>Familia:</strong> {idCard.Familia}
                </Typography>
                <Typography>
                  <strong>Subcategoría:</strong> {idCard.Subcategora}
                </Typography>
                <Typography>
                  <strong>Precio:</strong> {formatPrice(idCard.Precio_Actual)}
                </Typography>
              </CardContent>
              <CardActions className={classes.cardActions}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    handleAgregar({
                      repuesto_id: idCard.id,
                      nombre_repuesto: idCard.Descripcion_Material,
                      precio_repuesto: idCard.Precio_Actual,
                    })
                  }}
                >
                  Agregar
                </Button>
              </CardActions>
            </Card>
          )}
        </Paper>
      </Modal>

      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <Paper className={classes.modal}>
          <Typography variant="h6" className={classes.header}>
            Editar Recompensa
          </Typography>
          {selectedProduct && (
            <>
              <Box mb={2}>
                <Typography>
                  <strong>Producto:</strong> {selectedProduct.nombre_repuesto}
                </Typography>
                <Typography>
                  <strong>ID:</strong> {selectedProduct.repuesto_id}
                </Typography>
                <Typography>
                  <strong>Recompensa Actual:</strong>{' '}
                  {formatPrice(selectedProduct.recompensa)}
                </Typography>
              </Box>
              <TextField
                fullWidth
                label="Nueva Recompensa"
                variant="outlined"
                type="number"
                value={newReward}
                onChange={(e) => setNewReward(e.target.value)}
                margin="normal"
              />
              <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button
                  variant="outlined"
                  onClick={() => setEditModalOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleEditReward}
                >
                  Guardar
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Modal>
    </div>
  )
}

export default RutaProductosRecompensasContainer 